<template>
    <div>
        <card-header title="Sales Tax Report" icon="fa-chart-bar" />

        <card-toolbar>
            <button @click="exportSummaryCsv" :disabled="exportingSummary" class="mr-2">
                <i class="fas fa-file-csv"></i>
                {{ exportingSummary ? 'Exporting...' : 'Export Summary' }}
            </button>
            <button @click="exportCsv" :disabled="exporting">
                <i class="fas fa-download"></i>
                {{ exporting ? 'Exporting...' : 'Export Transactions' }}
            </button>
        </card-toolbar>

        <card-body>
            <!-- Summary Section -->
            <container-list-item padded class="pt-3 pb-3" v-if="reportData.summary">
                <div class="summary-grid">
                    <div class="summary-stat">
                        <div class="stat-label">Total Transactions</div>
                        <div class="stat-value">{{ reportData.summary.total_transactions }}</div>
                    </div>
                    <div class="summary-stat">
                        <div class="stat-label">Tax Collected</div>
                        <div class="stat-value has-text-success">{{ formatCurrency(reportData.summary.total_tax_collected) }}</div>
                    </div>
                </div>
            </container-list-item>

            <card-list>

                <div v-if="loading" class="has-text-centered py-5">
                    <b-loading :is-full-page="false" v-model="loading" :can-cancel="false"></b-loading>
                </div>

                <div v-else-if="reportData.transactions.length === 0" class="has-text-centered py-5 has-text-grey-light">
                    <i class="fas fa-search fa-3x mb-3"></i>
                    <p>No transactions found for the selected filters</p>
                </div>

                <template v-else>
                    <template v-for="(transactions, type) in groupedTransactions">
                        <subheader-list-item
                            :title="type"
                            :icon="getIconForType(type)"
                            :badge="getGroupBadge(transactions)"/>

                        <sales-tax-transaction-tile
                            v-for="transaction in transactions"
                            :key="transaction.transaction_id"
                            :transaction="transaction"/>
                    </template>
                </template>
            </card-list>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import SalesTaxTransactionTile from "@/components/tiles/SalesTaxTransactionTile";
    import {client as http} from "@/http_client";
    import numeral from "numeral";
    import moment from "moment";

    export default {
        components: {
            CardHeader,
            CardBody,
            CardToolbar,
            CardList,
            SubheaderListItem,
            ContainerListItem,
            SalesTaxTransactionTile
        },
        props: ['card', 'props'],
        data() {
            return {
                reportData: {
                    summary: {
                        total_transactions: 0,
                        total_tax_collected: 0,
                        total_tax_owed: 0
                    },
                    transactions: []
                },
                loading: true,
                exporting: false,
                exportingSummary: false
            }
        },
        mounted: function () {
            console.log('Results Card Mounted');
            console.log('Props received:', this.props);
            // Fetch report data based on filter props
            this.loadReport();
        },
        computed: {
            groupedTransactions: function() {
                const grouped = {};

                this.reportData.transactions.forEach(transaction => {
                    const type = transaction.transaction_type;
                    if (!grouped[type]) {
                        grouped[type] = [];
                    }
                    grouped[type].push(transaction);
                });

                return grouped;
            },

            getIconForType: function() {
                return (type) => {
                    const iconMap = {
                        'Payment Plan Taxes': 'fa-credit-card',
                        'Course Application Fee': 'fa-graduation-cap',
                        'Conference Application Fee': 'fa-clipboard-check',
                        'Conference Registration': 'fa-ticket-alt',
                        'Course Enrollment': 'fa-book',
                        'Reseller Cart': 'fa-shopping-cart',
                        'Course Reactivation': 'fa-redo',
                        'Other Transaction': 'fa-receipt'
                    };
                    return iconMap[type] || 'fa-file-invoice-dollar';
                };
            }
        },
        watch: {
            props: {
                handler: function() {
                    console.log('Props changed, reloading report');
                    this.loadReport();
                },
                deep: true
            }
        },
        methods: {
            getGroupBadge: function(transactions) {
                const count = transactions.length;
                const total = transactions.reduce((sum, t) => sum + (t.tax_paid || 0), 0);
                return `${count} - ${this.formatCurrency(total)}`;
            },

            reload: function() {
                this.loadReport();
            },

            loadReport: function() {
                this.$emit('loading', true);
                this.loading = true;

                // Split pipe-separated transaction types back into array
                let transactionTypes = [];
                if (this.props && this.props.transactionTypes) {
                    transactionTypes = this.props.transactionTypes.split('|').filter(t => t.length > 0);
                }

                const payload = {
                    startDate: this.props.startDate || null,
                    endDate: this.props.endDate || null,
                    transactionTypes: transactionTypes
                };

                console.log('Sending payload to API:', payload);

                http.post('sales-tax/consolidated-report', payload).then(response => {
                    this.reportData = response.data;
                    this.loading = false;
                    this.$emit('loading', false);
                }).catch(err => {
                    console.error('Error loading report:', err);
                    this.$buefy.toast.open({
                        message: 'Failed to load report data',
                        type: 'is-danger'
                    });
                    this.loading = false;
                    this.$emit('loading', false);
                });
            },

            formatCurrency: function(value) {
                return numeral(value).format('$0,0.00');
            },

            exportSummaryCsv: function() {
                this.$emit('loading', true);
                this.exportingSummary = true;

                // Split pipe-separated transaction types back into array
                let transactionTypes = [];
                if (this.props && this.props.transactionTypes) {
                    transactionTypes = this.props.transactionTypes.split('|').filter(t => t.length > 0);
                }

                const payload = {
                    startDate: this.props.startDate || null,
                    endDate: this.props.endDate || null,
                    transactionTypes: transactionTypes
                };

                http.post('sales-tax/consolidated-report/summary-csv', payload, {
                    responseType: 'blob'
                }).then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    // Build filename with date range
                    let dateRange = 'all';
                    if (this.props.startDate && this.props.endDate) {
                        dateRange = `${moment(this.props.startDate).format('M-D-YYYY')}-to-${moment(this.props.endDate).format('M-D-YYYY')}`;
                    } else if (this.props.startDate) {
                        dateRange = `from-${moment(this.props.startDate).format('M-D-YYYY')}`;
                    } else if (this.props.endDate) {
                        dateRange = `thru-${moment(this.props.endDate).format('M-D-YYYY')}`;
                    }
                    link.setAttribute('download', `sales-tax-summary-${dateRange}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    this.$emit('loading', false);
                    this.exportingSummary = false;
                }).catch(err => {
                    console.error('Error exporting summary CSV:', err);
                    this.$buefy.toast.open({
                        message: 'Failed to export summary CSV',
                        type: 'is-danger'
                    });
                    this.$emit('loading', false);
                    this.exportingSummary = false;
                });
            },

            exportCsv: function() {
                this.$emit('loading', true);
                this.exporting = true;

                // Split pipe-separated transaction types back into array
                let transactionTypes = [];
                if (this.props && this.props.transactionTypes) {
                    transactionTypes = this.props.transactionTypes.split('|').filter(t => t.length > 0);
                }

                const payload = {
                    startDate: this.props.startDate || null,
                    endDate: this.props.endDate || null,
                    transactionTypes: transactionTypes
                };

                http.post('sales-tax/consolidated-report/csv', payload, {
                    responseType: 'blob'
                }).then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    // Build filename with date range
                    let dateRange = 'all';
                    if (this.props.startDate && this.props.endDate) {
                        dateRange = `${moment(this.props.startDate).format('M-D-YYYY')}-to-${moment(this.props.endDate).format('M-D-YYYY')}`;
                    } else if (this.props.startDate) {
                        dateRange = `from-${moment(this.props.startDate).format('M-D-YYYY')}`;
                    } else if (this.props.endDate) {
                        dateRange = `thru-${moment(this.props.endDate).format('M-D-YYYY')}`;
                    }
                    link.setAttribute('download', `sales-tax-detail-${dateRange}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    this.$emit('loading', false);
                    this.exporting = false;
                }).catch(err => {
                    console.error('Error exporting CSV:', err);
                    this.$buefy.toast.open({
                        message: 'Failed to export CSV',
                        type: 'is-danger'
                    });
                    this.$emit('loading', false);
                    this.exporting = false;
                });
            }
        }
    }
</script>

<style lang="scss" scoped>
    .summary-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;

        .summary-stat {
            text-align: center;

            .stat-label {
                font-size: 0.85rem;
                color: #7a7a7a;
                margin-bottom: 0.5rem;
            }

            .stat-value {
                font-size: 1.3rem;
                font-weight: bold;
            }
        }
    }
</style>
